import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { ActionButton, BalanceCard, OrderCard, money } from '@shared/commerce';
import { useAuth } from '../../context/AuthContext';
import { canteenAPI, walletAPI } from '../../services/walletApi';
import type { CanteenOrder, Wallet } from '../../types/wallet';
import { ordersServedToday, todaysOrder } from '../../utils/canteenCart';
import { firstName, walletStatusNotice } from '../../utils/walletFormat';

/**
 * The selected child's wallet on the home screen (design screen 1, owner 19 Oct 2026): the
 * balance with Top up and Statement, today's canteen order, and two big buttons - Order food
 * and Wallet. Drawn by the shared customer kit, so the Student app shows the same later.
 * Draws nothing when wallets are off or the child has no wallet; a failed read draws nothing
 * either, since the Wallet tile still opens the full screen.
 */
export function HomeWalletSection({ onOpenWallet, onOrderFood, refreshKey }: {
  onOpenWallet: () => void; onOrderFood: () => void; refreshKey?: number;
}) {
  const { selectedStudent } = useAuth();
  const studentId = selectedStudent?.id;
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [order, setOrder] = useState<CanteenOrder | null>(null);
  const [othersToday, setOthersToday] = useState(0);

  const load = useCallback(async () => {
    try {
      const mine = await walletAPI.getMyWallets();
      // Only the selected child's own wallet - never another child's by fallback.
      const found = mine.enabled && studentId
        ? mine.wallets.find(w => w.ownerType === 'STUDENT' && w.ownerId.toLowerCase() === studentId.toLowerCase()) ?? null
        : null;
      setWallet(found);
      const orders = found && studentId ? await canteenAPI.getOrders(studentId) : [];
      setOrder(todaysOrder(orders, new Date()));
      // Breakfast and lunch are separate orders - say so, since the card shows one.
      setOthersToday(Math.max(0, ordersServedToday(orders, new Date()).length - 1));
    } catch {
      setWallet(null);
      setOrder(null);
      setOthersToday(0);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!wallet) { return null; }
  const notice = walletStatusNotice(wallet);

  return (
    <View style={styles.section} testID="home-wallet-section">
      <BalanceCard testID="home-wallet-balance"
        label={`${firstName(wallet.ownerName)}'s wallet · ${wallet.payerId}`}
        amount={wallet.cashBalance} currency={wallet.currency}
        meta={notice ?? `Low-balance alert at ${money(wallet.lowBalanceAlert, wallet.currency)}`}
        actions={[
          { label: 'Top up', onPress: onOpenWallet, primary: true, testID: 'home-wallet-topup' },
          { label: 'Statement', onPress: onOpenWallet, testID: 'home-wallet-statement' },
        ]} />
      {order && (
        <OrderCard testID="home-todays-order" title="Today's order" orderStatus={order.orderStatus}
          lines={order.items.map(l => `${l.quantity} × ${l.itemName}`).join(', ')}
          total={order.totalAmount} currency={wallet.currency} showTracker={false}
          footer={`${order.orderNumber}${othersToday > 0 ? ` · ${othersToday} more today` : ''}`} />
      )}
      <View style={styles.buttons}>
        <ActionButton label="Order food" tone="orange" onPress={onOrderFood} testID="home-order-food" />
        <ActionButton label="Wallet" tone="teal" onPress={onOpenWallet} testID="home-open-wallet" />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  // The gap below is the Home screen's (moneyBlock), so it is there with or without a wallet.
  section: { marginHorizontal: 16, marginTop: 12, gap: 10 },
  buttons: { flexDirection: 'row', gap: 10 },
});
